import { supabase } from "../supabase/SupabaseClient";

/* Proveedores › Facturas a pagar: lo que la pantalla le pide a la base.
 *
 * ── TODO LO DECIDE LA BASE ─────────────────────────────────────────────────
 *
 * La bandeja (`facturas_a_pagar()`) ya viene armada: el estado de cada
 * factura, sus copias repetidas, los avisos y lo que hay que confirmar para
 * autorizarla. Mover una factura es UNA llamada (`factura_pago_mover`), que
 * chequea la llave, el paso, los motivos, las repetidas y que nadie la haya
 * tocado mientras se miraba (versión y huella). Esta pantalla no repite
 * ninguna de esas reglas: si la base dice que no, se muestra lo que dijo.
 *
 * ── SI LA MIGRACIÓN TODAVÍA NO SE APLICÓ ───────────────────────────────────
 *
 * Nace con `20261020025324_proveedores_facturas_a_pagar.sql`. Hasta que esté
 * aplicada, PostgREST contesta que la función no existe; eso no es «no hay
 * facturas» ni «se cayó la base», y se dice distinto (`FaltaMigracion`). El
 * chip de «Facturas y remitos» falla callado: sin migración, no hay chips.
 */

export class FaltaMigracion extends Error {
  constructor() {
    super("Todavía no está creada la bandeja de facturas a pagar.");
    this.name = "FaltaMigracion";
  }
}

/* PGRST202: función que no está · PGRST205: tabla que no está · 42P01 /
 * 42883: no existe. */
export const esFaltaMigracion = (error) =>
  Boolean(error) &&
  (["PGRST202", "PGRST205", "42P01", "42883"].includes(error.code) ||
    /could not find the (table|function)|does not exist/i.test(error.message || ""));

/* El error de la base, con su código: la pantalla distingue 40001 (alguien la
 * movió o la corrigió mientras se miraba) y 42501 (sin la llave). Cuando
 * falta confirmar algo, la base manda la lista (pista «revisiones», detalle
 * con las claves): la pantalla dibuja esas casillas aunque no las haya pedido
 * antes. */
export const lanzar = (error) => {
  if (esFaltaMigracion(error)) throw new FaltaMigracion();
  const e = new Error(error?.message || "No se pudo.");
  e.code = error?.code;
  if (error?.hint === "revisiones") {
    try {
      const l = JSON.parse(error.details || "[]");
      if (Array.isArray(l)) e.faltan = l.map(String);
    } catch {
      /* sin la lista: queda el texto */
    }
  }
  throw e;
};

/* La bandeja viene en UN valor (un arreglo): con una fila por factura,
 * PostgREST la cortaría en 1.000 sin avisar (ver services/paginar.js). */
export async function traerBandeja() {
  const { data, error } = await supabase.rpc("facturas_a_pagar");
  if (error) lanzar(error);
  return Array.isArray(data) ? data : [];
}

/* Mover una factura. Viaja la versión y la huella con las que se la miró. */
export async function mover(fila, accion, datos = {}) {
  const { data, error } = await supabase.rpc("factura_pago_mover", {
    p_origen: fila.origen,
    p_ref_id: fila.ref_id,
    p_accion: accion,
    p_version: fila.version ?? 0,
    p_huella: fila.huella ?? null,
    p_datos: datos,
  });
  if (error) lanzar(error);
  return data;
}

/* Lo que la base va a pedir para un paso con plata con el CUIT y el número
 * que se tipearon (una factura sin clave): `{ revisiones, bloqueo, clave,
 * cuit_nuestro }`. La bandeja lo calcula sin ellos. */
export async function traerQuePide(fila, accion, cuit, numero) {
  const { data, error } = await supabase.rpc("factura_pago_que_pide", {
    p_origen: fila.origen,
    p_ref_id: fila.ref_id,
    p_accion: accion,
    p_cuit: cuit || null,
    p_numero: numero || null,
  });
  if (error) lanzar(error);
  return data || { revisiones: [] };
}

/* El registro de una factura, del primer paso al último. */
export async function traerPasos(origen, refId) {
  const { data, error } = await supabase
    .from("factura_pago_historial")
    .select("id,accion,estado_antes,estado_despues,motivo,nota,pedida_a,importe_factura," +
            "importe_autorizado,moneda,forma_pago,detalle,hecho_por_nombre,hecho_en")
    .eq("origen", origen)
    .eq("ref_id", refId)
    .order("id", { ascending: true });
  if (error) lanzar(error);
  return data || [];
}

/* Quiénes autorizan pagos (los que tienen la llave): sale de la base, así se
 * cambia desde Sistema › Permisos sin tocar la pantalla. */
export async function traerAutorizan() {
  const { data, error } = await supabase.rpc("proveedores_autorizan");
  if (error) lanzar(error);
  return data || [];
}

/* El sector de cada uno que autoriza (20261022071424): `[{ mail, sector,
 * orden }]`, una fila por persona y sector. Sólo orienta en «Pedir
 * autorización a…»; quién autoriza lo dice `traerAutorizan`. NUNCA tira: sin
 * la migración (o sin red) es [], y la lista se ve como antes, sin sectores. */
export async function traerSectores() {
  try {
    const { data, error } = await supabase
      .from("proveedores_autorizantes")
      .select("mail,sector,orden")
      .order("orden", { ascending: true });
    if (error) return [];
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export async function traerFormasDePago() {
  const { data, error } = await supabase
    .from("proveedores_formas_pago")
    .select("clave,etiqueta,orden,activa")
    .order("orden", { ascending: true });
  if (error) lanzar(error);
  return data || [];
}

export async function traerConfig() {
  const { data, error } = await supabase
    .from("proveedores_config")
    .select("arranque,separar_funciones")
    .maybeSingle();
  if (error) lanzar(error);
  return data || null;
}

/* Las facturas fijas (20261022155856_proveedores_facturas_fijas.sql): qué
 * proveedor es fijo, con qué categoría, y de cada factura suya cuánto subió
 * contra la anterior. `{ umbral, categorias, fijos, facturas }`.
 *
 * NUNCA tira, como los chips: si falla (la migración todavía no está, la
 * red), devuelve null y la bandeja se ve como antes, sin las marcas. Un error
 * acá no puede dejar a nadie sin la bandeja de pagos. */
export async function traerFijas() {
  try {
    const { data, error } = await supabase.rpc("facturas_fijas");
    if (error) return null;
    return data && typeof data === "object" && !Array.isArray(data) ? data : null;
  } catch {
    return null;
  }
}

/* Marcar a un proveedor (por CUIT) como fijo con esa categoría, cambiarla, o
 * desmarcarlo (`categoria` vacía). La base chequea las llaves y el CUIT, y
 * deja el paso en su registro. */
export async function marcarFijo(cuit, categoria, nota = "") {
  const { data, error } = await supabase.rpc("proveedor_fijo_marcar", {
    p_cuit: cuit,
    p_categoria: categoria || null,
    p_nota: nota || null,
  });
  if (error) lanzar(error);
  return data;
}

/* Los chips de «Facturas y remitos». NUNCA tira: si falla por lo que sea (la
 * migración no está, la red), no hay chips y el resto de esa pantalla sigue
 * igual. */
export async function traerChips() {
  try {
    const { data, error } = await supabase.rpc("facturas_pago_chips");
    if (error) return [];
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}
